import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { calculateWeeks } from '@/lib/dateUtils';
import { Grid3X3, User, ExternalLink } from 'lucide-react';
import { parseISO, addWeeks, format, startOfWeek as dateFnsStartOfWeek, getQuarter, getYear } from 'date-fns';

const ELIGIBLE_STATUSES = ['sow', 'execute'];

const weekKey = (date) => {
  const d = typeof date === 'string' ? parseISO(date) : date;
  return format(dateFnsStartOfWeek(d, { weekStartsOn: 1 }), 'yyyy-MM-dd');
};

const projectWeekKeys = (startDate, numWeeks) => {
  const start = typeof startDate === 'string' ? parseISO(startDate) : startDate;
  const keys = [];
  for (let i = 0; i < numWeeks; i++) keys.push(weekKey(addWeeks(start, i)));
  return keys;
};

const getCellColor = (val) => {
  if (val === 0) return 'bg-zinc-900/50 text-zinc-600';
  if (val < 0.8) return 'bg-red-500/20 text-red-300 border-red-500/30';
  if (val <= 1.0) return 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30';
  return 'bg-amber-500/20 text-amber-300 border-amber-500/30';
};

const getStatusLabel = (avgUtil) => {
  if (avgUtil < 0.8) return { label: 'Under', key: 'under', color: 'text-red-400 bg-red-400/10 border-red-400/20' };
  if (avgUtil <= 1.0) return { label: 'Optimal', key: 'optimal', color: 'text-emerald-400 bg-emerald-400/10 border-emerald-400/20' };
  return { label: 'Over', key: 'over', color: 'text-amber-400 bg-amber-400/10 border-amber-400/20' };
};

// Get quarter start month (1-indexed): Q1=Jan, Q2=Apr, Q3=Jul, Q4=Oct
const QUARTER_MONTHS = { 1: [0, 1, 2], 2: [3, 4, 5], 3: [6, 7, 8], 4: [9, 10, 11] };

const getAvailableYears = (sortedWeeks) => {
  const years = new Set();
  sortedWeeks.forEach(wk => years.add(getYear(parseISO(wk))));
  return [...years].sort();
};

const getCurrentQuarter = () => getQuarter(new Date());
const getCurrentYear = () => getYear(new Date());

export function ResourceHeatmap({ projects }) {
  const navigate = useNavigate();
  const [utilizationFilter, setUtilizationFilter] = useState('all');
  const [selectedQuarter, setSelectedQuarter] = useState(getCurrentQuarter());
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());

  const heatmapData = useMemo(() => {
    const eligible = projects.filter(p => ELIGIBLE_STATUSES.includes(p.status));
    let allWeekKeys = new Set();

    const entries = [];
    eligible.forEach(p => {
      if (!p.startDate || !p.endDate) return;
      const numWeeks = calculateWeeks(p.startDate, p.endDate);
      const wKeys = projectWeekKeys(p.startDate, numWeeks);
      wKeys.forEach(k => allWeekKeys.add(k));

      const actuals = (p.actualResources || []).length > 0 ? p.actualResources : p.resources || [];
      actuals.forEach(res => {
        const personName = res.name && res.name.trim() ? res.name.trim() : null;
        if (!personName) return;
        const alloc = res.weeklyAllocation || [];

        const actualWKeys = [];
        for (let i = 0; i < alloc.length; i++) {
          if (i < wKeys.length) actualWKeys.push(wKeys[i]);
          else {
            const spillKey = weekKey(addWeeks(parseISO(p.startDate), i));
            actualWKeys.push(spillKey);
            allWeekKeys.add(spillKey);
          }
        }

        entries.push({
          name: personName,
          type: res.label || res.type,
          project: p.projectName || p.id,
          projectId: p.id,
          weekEntries: actualWKeys.map((wk, i) => ({ wk, val: alloc[i] || 0 })),
        });
      });
    });

    const sortedWeeks = [...allWeekKeys].sort();

    const personMap = {};
    entries.forEach(({ name, type, project, projectId, weekEntries }) => {
      if (!personMap[name]) {
        personMap[name] = { name, types: new Set(), projects: [], weekTotals: {}, weekDetails: {} };
      }
      personMap[name].types.add(type);
      if (!personMap[name].projects.find(p => p.id === projectId)) {
        personMap[name].projects.push({ id: projectId, name: project });
      }
      weekEntries.forEach(({ wk, val }) => {
        personMap[name].weekTotals[wk] = (personMap[name].weekTotals[wk] || 0) + val;
        if (!personMap[name].weekDetails[wk]) personMap[name].weekDetails[wk] = [];
        if (val > 0) personMap[name].weekDetails[wk].push({ project, projectId, val });
      });
    });

    const persons = Object.values(personMap).map(p => ({
      name: p.name,
      types: [...p.types].join(', '),
      projects: p.projects,
      weekTotals: sortedWeeks.map(wk => p.weekTotals[wk] || 0),
      weekDetails: sortedWeeks.map(wk => p.weekDetails[wk] || []),
    }));

    persons.sort((a, b) => a.name.localeCompare(b.name));

    const weekLabels = sortedWeeks.map(wk => format(parseISO(wk), 'MMM d'));
    const availableYears = getAvailableYears(sortedWeeks);
    return { persons, weekLabels, sortedWeeks, availableYears };
  }, [projects]);

  const { persons, weekLabels, sortedWeeks, availableYears } = heatmapData;

  // Filter weeks by selected quarter
  const quarterMonths = QUARTER_MONTHS[selectedQuarter];
  const filteredWeekIndices = useMemo(() => {
    return sortedWeeks.map((wk, i) => {
      const d = parseISO(wk);
      return getYear(d) === selectedYear && quarterMonths.includes(d.getMonth()) ? i : -1;
    }).filter(i => i !== -1);
  }, [sortedWeeks, selectedYear, quarterMonths]);

  // Compute avg per person using only filtered weeks, then apply utilization filter
  const filteredPersons = useMemo(() => {
    return persons.map(person => {
      const qTotals = filteredWeekIndices.map(i => person.weekTotals[i] || 0);
      const nonZero = qTotals.filter(v => v > 0);
      const avg = nonZero.length > 0 ? nonZero.reduce((s, v) => s + v, 0) / nonZero.length : 0;
      const statusInfo = getStatusLabel(avg);
      return { ...person, avg, statusInfo };
    }).filter(person => {
      if (utilizationFilter === 'all') return true;
      return person.statusInfo.key === utilizationFilter;
    });
  }, [persons, filteredWeekIndices, utilizationFilter]);

  if (persons.length === 0) {
    return (
      <Card className="bg-zinc-950 border-zinc-900">
        <CardContent className="p-12 text-center">
          <Grid3X3 className="w-10 h-10 mx-auto mb-4 text-zinc-700" />
          <h3 className="font-manrope font-bold text-lg text-zinc-300 mb-2">No Tagged Resources</h3>
          <p className="text-zinc-500 text-sm">Tag person names in "Actual v/s Planned" to see the resource heatmap.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="resource-heatmap">
      {/* Header + Filters */}
      <Card className="bg-zinc-950 border-zinc-900">
        <CardContent className="p-3 space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Grid3X3 className="w-4 h-4 text-brand-400" />
              <h3 className="font-manrope font-bold text-sm text-white">Resource Utilization Heatmap</h3>
              <span className="text-zinc-500 text-xs">— {filteredPersons.length} of {persons.length} resource{persons.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="flex items-center gap-3 text-[10px] uppercase tracking-widest font-manrope">
              <div className="flex items-center gap-1"><div className="w-4 h-3 rounded-sm bg-zinc-900/50 border border-zinc-800"></div><span className="text-zinc-500">Idle</span></div>
              <div className="flex items-center gap-1"><div className="w-4 h-3 rounded-sm bg-red-500/20 border border-red-500/30"></div><span className="text-red-400">&lt;0.8</span></div>
              <div className="flex items-center gap-1"><div className="w-4 h-3 rounded-sm bg-emerald-500/20 border border-emerald-500/30"></div><span className="text-emerald-400">0.8–1.0</span></div>
              <div className="flex items-center gap-1"><div className="w-4 h-3 rounded-sm bg-amber-500/20 border border-amber-500/30"></div><span className="text-amber-400">&gt;1.0</span></div>
            </div>
          </div>

          {/* Filter bar */}
          <div className="flex items-center gap-4 flex-wrap">
            {/* Utilization filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-manrope">Status:</span>
              {[
                { key: 'all', label: 'All', color: 'text-zinc-300' },
                { key: 'under', label: 'Under', color: 'text-red-400' },
                { key: 'optimal', label: 'Optimal', color: 'text-emerald-400' },
                { key: 'over', label: 'Over', color: 'text-amber-400' },
              ].map(f => (
                <Button
                  key={f.key}
                  size="sm"
                  variant={utilizationFilter === f.key ? 'default' : 'ghost'}
                  onClick={() => setUtilizationFilter(f.key)}
                  data-testid={`heatmap-filter-${f.key}`}
                  className={utilizationFilter === f.key
                    ? 'bg-brand-500 text-white hover:bg-brand-400 text-[11px] h-6 px-2.5 font-manrope'
                    : `${f.color} hover:bg-zinc-900 text-[11px] h-6 px-2.5 font-manrope`
                  }
                >
                  {f.label}
                </Button>
              ))}
            </div>

            {/* Quarter filter */}
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-manrope">Quarter:</span>
              {[1, 2, 3, 4].map(q => (
                <Button
                  key={q}
                  size="sm"
                  variant={selectedQuarter === q ? 'default' : 'ghost'}
                  onClick={() => setSelectedQuarter(q)}
                  data-testid={`heatmap-quarter-${q}`}
                  className={selectedQuarter === q
                    ? 'bg-brand-500 text-white hover:bg-brand-400 text-[11px] h-6 px-2.5 font-mono'
                    : 'text-zinc-400 hover:text-white hover:bg-zinc-900 text-[11px] h-6 px-2.5 font-mono'
                  }
                >
                  Q{q}
                </Button>
              ))}
            </div>

            {/* Year selector */}
            {availableYears.length > 0 && (
              <div className="flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-manrope">Year:</span>
                <Select value={String(selectedYear)} onValueChange={v => setSelectedYear(Number(v))}>
                  <SelectTrigger className="bg-zinc-950 border-zinc-800 h-6 text-[11px] w-20 font-mono" data-testid="heatmap-year-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {availableYears.map(y => (
                      <SelectItem key={y} value={String(y)} className="text-zinc-200 focus:bg-zinc-900 focus:text-white text-xs font-mono">{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Heatmap Table */}
      <Card className="bg-zinc-950 border-zinc-900">
        <CardContent className="p-0 overflow-x-auto">
          <TooltipProvider delayDuration={150}>
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="sticky left-0 z-10 bg-zinc-950 min-w-[200px] text-xs uppercase tracking-widest text-zinc-500 font-manrope">Person</TableHead>
                  <TableHead className="min-w-[120px] text-xs uppercase tracking-widest text-zinc-500 font-manrope">Role</TableHead>
                  <TableHead className="min-w-[50px] text-xs uppercase tracking-widest text-zinc-500 font-manrope text-center">Proj</TableHead>
                  {filteredWeekIndices.map(wi => (
                    <TableHead key={wi} className="min-w-[56px] text-[10px] uppercase tracking-widest text-zinc-500 font-manrope text-center px-1">
                      {weekLabels[wi]}
                    </TableHead>
                  ))}
                  <TableHead className="min-w-[60px] text-xs uppercase tracking-widest text-zinc-500 font-manrope text-center">Avg</TableHead>
                  <TableHead className="min-w-[70px] text-xs uppercase tracking-widest text-zinc-500 font-manrope text-center">Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPersons.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5 + filteredWeekIndices.length} className="text-center py-8 text-zinc-500 font-manrope text-sm">
                      No resources match the selected filters
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredPersons.map((person) => (
                    <TableRow key={person.name} className="border-zinc-900 hover:bg-zinc-900/30" data-testid={`heatmap-row-${person.name.replace(/\s+/g, '-').toLowerCase()}`}>
                      <TableCell className="sticky left-0 z-10 bg-zinc-950">
                        {person.projects.length === 1 ? (
                          <button
                            onClick={() => navigate(`/estimate/${person.projects[0].id}`, { state: { from: 'heatmap' } })}
                            className="flex items-center gap-1.5 group cursor-pointer"
                            data-testid={`heatmap-nav-${person.name.replace(/\s+/g, '-').toLowerCase()}`}
                          >
                            <User className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                            <span className="font-manrope text-sm text-white font-medium group-hover:text-brand-400 transition-colors">{person.name}</span>
                            <ExternalLink className="w-3 h-3 text-zinc-600 group-hover:text-brand-400 transition-colors" />
                          </button>
                        ) : (
                          <Popover>
                            <PopoverTrigger asChild>
                              <button
                                className="flex items-center gap-1.5 group cursor-pointer"
                                data-testid={`heatmap-nav-${person.name.replace(/\s+/g, '-').toLowerCase()}`}
                              >
                                <User className="w-3.5 h-3.5 text-brand-400 shrink-0" />
                                <span className="font-manrope text-sm text-white font-medium group-hover:text-brand-400 transition-colors">{person.name}</span>
                                <ExternalLink className="w-3 h-3 text-zinc-600 group-hover:text-brand-400 transition-colors" />
                              </button>
                            </PopoverTrigger>
                            <PopoverContent className="bg-zinc-950 border-zinc-800 p-1 w-auto min-w-[180px]" align="start">
                              <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-manrope px-2 py-1">Go to project</p>
                              {person.projects.map(proj => (
                                <button
                                  key={proj.id}
                                  onClick={() => navigate(`/estimate/${proj.id}`, { state: { from: 'heatmap' } })}
                                  className="w-full text-left px-2 py-1.5 text-sm font-manrope text-zinc-300 hover:text-white hover:bg-zinc-900 rounded flex items-center justify-between gap-2"
                                  data-testid={`heatmap-nav-${person.name.replace(/\s+/g, '-').toLowerCase()}-${proj.id}`}
                                >
                                  <span>{proj.name}</span>
                                  <span className="font-mono text-[10px] text-brand-400">{proj.id}</span>
                                </button>
                              ))}
                            </PopoverContent>
                          </Popover>
                        )}
                      </TableCell>
                      <TableCell className="font-manrope text-xs text-zinc-400">{person.types}</TableCell>
                      <TableCell className="text-center">
                        <span className="font-mono text-xs text-zinc-300">{person.projects.length}</span>
                      </TableCell>
                      {filteredWeekIndices.map(wi => {
                        const val = person.weekTotals[wi] || 0;
                        const details = person.weekDetails[wi] || [];
                        return (
                          <TableCell key={wi} className="p-0.5 text-center">
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className={`rounded-sm px-1 py-1 font-mono text-[11px] font-medium border cursor-default ${getCellColor(val)}`}>
                                  {val > 0 ? val.toFixed(1) : '—'}
                                </div>
                              </TooltipTrigger>
                              {details.length > 0 && (
                                <TooltipContent className="bg-zinc-900 border-zinc-700 p-2 max-w-[220px]">
                                  <div className="space-y-1">
                                    {details.map((d, di) => (
                                      <div key={di} className="flex items-center justify-between gap-3 text-xs">
                                        <span className="text-zinc-300 font-manrope truncate">{d.project}</span>
                                        <span className="font-mono text-brand-400 shrink-0">{d.val.toFixed(1)}</span>
                                      </div>
                                    ))}
                                    {details.length > 1 && (
                                      <div className="flex items-center justify-between gap-3 text-xs border-t border-zinc-700 pt-1 mt-1">
                                        <span className="text-zinc-500 font-manrope">Total</span>
                                        <span className="font-mono text-white font-semibold">{val.toFixed(1)}</span>
                                      </div>
                                    )}
                                  </div>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          </TableCell>
                        );
                      })}
                      <TableCell className="text-center">
                        <span className="font-mono text-xs font-semibold text-zinc-200">{person.avg.toFixed(2)}</span>
                      </TableCell>
                      <TableCell className="text-center">
                        <Badge className={`${person.statusInfo.color} border text-[10px] rounded-full`}>{person.statusInfo.label}</Badge>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TooltipProvider>
        </CardContent>
      </Card>
    </div>
  );
}
